import { useState } from "react";
import { Star, Loader2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={onChange ? () => onChange(n) : undefined} disabled={!onChange}
          className={onChange ? "cursor-pointer" : "cursor-default"} aria-label={`${n} star`} data-testid={onChange ? `review-star-${n}` : undefined}>
          <Star size={size} className={n <= value ? "fill-saddle text-saddle" : "text-charcoal/25"} />
        </button>
      ))}
    </div>
  );
}

export function ProductReviews({ product, onSubmitted }) {
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const reviews = product.reviews || [];

  const submit = async () => {
    if (!body.trim()) { toast.error("Please write a few words about the product"); return; }
    setBusy(true);
    try {
      await api.shopCreateReview(product.id, { rating, title: title.trim(), body: body.trim() });
      toast.success("Thanks — your review is live");
      setTitle(""); setBody(""); setRating(5); setShowForm(false);
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit your review"); }
    finally { setBusy(false); }
  };

  return (
    <section className="max-w-[1300px] mx-auto px-5 sm:px-10 py-12 border-t border-charcoal/10" data-testid="pd-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl text-charcoal tracking-tight">Reviews</h2>
          {product.review_count > 0 ? (
            <div className="flex items-center gap-2 mt-2" data-testid="pd-reviews-summary">
              <Stars value={Math.round(product.rating)} />
              <span className="text-sm text-charcoal/60">{product.rating} · {product.review_count} review{product.review_count === 1 ? "" : "s"}</span>
            </div>
          ) : (
            <p className="text-sm text-charcoal/50 mt-2" data-testid="pd-no-reviews">No reviews yet — be the first to share your thoughts.</p>
          )}
        </div>
        <button
          onClick={() => (isAuthed ? setShowForm((v) => !v) : openAuth?.("login"))}
          className="border border-blaze text-blaze uppercase tracking-[0.16em] text-xs font-semibold px-6 py-3 hover:bg-blaze hover:text-white transition-colors"
          data-testid="write-review-btn">
          {isAuthed ? (showForm ? "Cancel" : "Write a review") : "Sign in to review"}
        </button>
      </div>

      {isAuthed && showForm && (
        <div className="bg-bone border border-charcoal/10 p-5 mt-5 max-w-xl" data-testid="review-form">
          <p className="hr-overline text-charcoal/50 mb-2">Your rating</p>
          <Stars value={rating} onChange={setRating} size={24} />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a headline (optional)"
            className="w-full border border-charcoal/20 bg-white px-3 py-2 text-sm outline-none focus:border-forest mt-4" data-testid="review-title-input" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="What did you think?"
            className="w-full border border-charcoal/20 bg-white px-3 py-2 text-sm outline-none focus:border-forest mt-3" data-testid="review-body-input" />
          <button onClick={submit} disabled={busy} className="bg-forest text-bone px-6 py-2.5 font-semibold text-sm mt-3 hover:bg-forest-2 transition-colors inline-flex items-center gap-2" data-testid="review-submit-btn">
            {busy && <Loader2 size={15} className="animate-spin" />} Post review
          </button>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 max-w-4xl" data-testid="review-list">
          {reviews.map((r) => (
            <div key={r.id} className="border border-charcoal/10 bg-white p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <Stars value={r.rating} size={15} />
                {r.verified_buyer && <span className="flex items-center gap-1 text-[11px] text-forest font-medium"><BadgeCheck size={13} /> Verified buyer</span>}
              </div>
              {r.title && <p className="font-medium text-charcoal mt-2 text-sm">{r.title}</p>}
              {r.body && <p className="text-charcoal/70 text-sm mt-1 leading-relaxed">{r.body}</p>}
              <p className="text-[11px] text-charcoal/40 mt-2">{r.author_name}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
